import React, { useCallback, useEffect, useReducer, useState } from 'react'
import { Icons } from '../../Shared/Icons'
import SuggestedCard from './SuggestedCard'
import { buildActions, isCombinedAction } from '../../../utils/aiAnalysis/draftPayload'
import type { DraftActionSet } from '../../../utils/aiAnalysis/draftPayload'
import { handoffStore } from '../../../utils/aiAnalysis/handoffStore'
import { AI_SECTION_EVENTS, markCardShown, trackSectionEvent } from '../../../utils/aiAnalysis/rumFunnel'
import { isDraftActionDecided } from '../../../models/aiAnalysis/workflow'
import type { DraftAction } from '../../../models/aiAnalysis/workflow'
import styles from './styles.module.scss'

export interface AiSuggestedSectionProps extends DraftActionSet {
  // Fired when a card's decision state goes stale (a fatal or
  // already-decided dismiss). The stale card is removed either way; the
  // hook is where a host refreshes its payload.
  onDecisionStateStale?: () => void
}

interface SectionCard {
  key: string
  action: DraftAction
}

const SuggestedSection: React.FC<AiSuggestedSectionProps> = ({
  forwardAction,
  acknowledgeAction,
  combinedToClientAction,
  combinedToNotaryAction,
  onDecisionStateStale
}) => {
  // "Close" in flow means the card leaves the section: after its last
  // decision, after a fatal dismiss, or after a boundary-caught crash.
  const [closedCardKeys, setClosedCardKeys] = useState<ReadonlySet<string>>(new Set())
  const [, bumpStoreVersion] = useReducer((version: number) => version + 1, 0)

  useEffect(() => handoffStore.subscribe(bumpStoreVersion), [])

  const closeCard = useCallback((cardKey: string) => {
    setClosedCardKeys((previous) => {
      const next = new Set(previous)
      next.add(cardKey)
      return next
    })
  }, [])

  const heldAction = handoffStore.getHeld()

  const isUndecided = (action: DraftAction) => !isDraftActionDecided(action) && !handoffStore.isDecided(action.id)

  const actions = buildActions({
    forwardAction,
    acknowledgeAction,
    combinedToClientAction,
    combinedToNotaryAction
  }).filter(isUndecided)

  // Combined actions supersede the forward/ack drafts server-side.
  const combinedActions = actions.filter(isCombinedAction)
  const cardActions = combinedActions.length > 0 ? combinedActions : actions

  const visibleCards: SectionCard[] = cardActions
    .map((action) => ({ key: action.id, action }))
    .filter((card) => !closedCardKeys.has(card.key) && card.key !== heldAction?.id)

  useEffect(() => {
    visibleCards
      .filter(({ action }) => markCardShown(action.id))
      .forEach(({ action }) => trackSectionEvent(AI_SECTION_EVENTS.CARD_SHOWN, { action_id: action.id }))
  }, [])

  if (visibleCards.length === 0) {
    return null
  }

  return (
    <section className={styles.section} aria-label="Suggested" data-test="ai-suggested-section">
      <div className={styles.header} aria-hidden="true">
        <Icons.Sparkle data-test="suggested-sparkle" />
        <span>Suggested</span>
      </div>
      {visibleCards.map((card) => (
        <SuggestedCard
          key={card.key}
          action={card.action}
          handoffInProgress={heldAction != null}
          onCardClose={() => closeCard(card.key)}
          onDecisionStateStale={onDecisionStateStale}
        />
      ))}
    </section>
  )
}

export default SuggestedSection
